import { Button } from "@9amhealth/shared";
import type { FC } from "react";
import { IconRxDietLogoPurple } from "src/constants/icons";
import { rxDietDescriptionListItems } from "src/constants/rxDietData";
import rxDietDescriptionDialogImgDesktop from "src/ui/assets/images/rxdiet-description-dialog-img-desktop.jpg";
import rxDietDescriptionDialogImgMobile from "src/ui/assets/images/rxdiet-description-dialog-img-mobile.jpg";
import Translate from "src/ui/components/Translate/Translate";
import { List } from "src/ui/styled/List";
import { StyledPill } from "./RxDietFirstScreen";
import { DisableAppPadding } from "src/ui/components/AppRouter/AppRouter";
import { rxDietDescriptionStepStyled } from "./RxDietDescriptionStep.styled";
import { useBlocNext } from "@blac/react";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import { useNavigate } from "react-router-dom";
import Path from "src/constants/path";

const {
  PageContainer,
  ContentGrid,
  ContentLeft,
  ContentRight,
  ContentImgMobile,
  ContentImgDesktop,
  StyledContainer,
  StyledListWrapper,
  StyledContainerInner,
  StyledListElement,
  StyledTaskButtonContainer
} = rxDietDescriptionStepStyled;

interface RxDietDescriptionStepProps {
  onContinue?: () => void;
}

const RxDietDescriptionStep: FC<RxDietDescriptionStepProps> = ({
  onContinue
}) => {
  const [_, { skipMealPlanTask }] = useBlocNext(TaskManagementBloc);
  const navigate = useNavigate();

  const onSkipTask = () => {
    void skipMealPlanTask().then(() => {
      navigate(`${Path.app}/${Path.home}`);
    });
  };

  return (
    <PageContainer>
      <DisableAppPadding />
      <ContentGrid>
        <ContentLeft>
          <ContentImgMobile
            src={rxDietDescriptionDialogImgMobile}
            alt=""
            aria-hidden="true"
          />
          <ContentImgDesktop
            src={rxDietDescriptionDialogImgDesktop}
            alt=""
            aria-hidden="true"
          />
        </ContentLeft>

        <ContentRight>
          <div>
            <StyledPill style={{ marginBottom: "0.75rem" }}>
              <small className="as-tiny">
                <Translate msg="poweredBy" />
              </small>
              <IconRxDietLogoPurple />
            </StyledPill>

            <h4 className="as-h4-large">
              <Translate msg="rxDiet.descriptionDialog.content.title" />
            </h4>
          </div>

          <StyledContainer>
            {rxDietDescriptionListItems.map((getListItem, listItemIndex) => {
              const listItem = getListItem();
              return (
                <StyledContainerInner key={listItem.id}>
                  <StyledListWrapper
                    className={
                      listItemIndex === rxDietDescriptionListItems.length - 1
                        ? "dashed-line"
                        : ""
                    }
                  >
                    <h6 className="as-body2">
                      {listItem.title}
                      {listItem.optional && (
                        <>
                          {" "}
                          <small
                            className="as-tiny color-c-60 strong"
                            style={{ textTransform: "lowercase" }}
                          >
                            (<Translate msg="optional" />)
                          </small>
                        </>
                      )}
                    </h6>

                    {Boolean(listItem.subtitles?.length) && (
                      <List className="m0">
                        {listItem.subtitles?.map((subtitle, subtitleIndex) => (
                          <StyledListElement
                            key={`rxdiet-descriptionStep-list-item-${listItemIndex}-subtitle-${subtitleIndex}`}
                            className={subtitleIndex === 0 ? "m0" : ""}
                          >
                            {subtitle}
                          </StyledListElement>
                        ))}
                      </List>
                    )}
                  </StyledListWrapper>
                </StyledContainerInner>
              );
            })}
          </StyledContainer>
          <StyledTaskButtonContainer>
            <Button
              theme="rxdiet"
              onPress={() => {
                onContinue?.();
              }}
            >
              <Translate msg="continue" />
            </Button>
            <Button theme="sunrise" data-outline="true" onPress={onSkipTask}>
              <Translate msg="task.skip.button_text" />
            </Button>
          </StyledTaskButtonContainer>
        </ContentRight>
      </ContentGrid>
    </PageContainer>
  );
};

export default RxDietDescriptionStep;
